import { TriangleAlert as AlertTriangle, Circle as XCircle, Info, Lightbulb } from 'lucide-react';
import type { TabCommonMistakes, DifficultyLevel } from '../../../../types/craft';
import FormattedText from '../../../common/FormattedText';

interface Props {
  data: TabCommonMistakes;
}

const SEVERITY_STYLES = {
  critical: { bg: 'bg-app-critical/10', border: 'border-app-critical', text: 'text-app-critical', label: 'Critical' },
  moderate: { bg: 'bg-app-warning/10', border: 'border-app-warning', text: 'text-app-warning', label: 'Moderate' },
  minor: { bg: 'bg-app-canvas', border: 'border-app-line', text: 'text-app-muted', label: 'Minor' },
};

const LEVEL_STYLES: Record<DifficultyLevel, { bg: string; text: string }> = {
  foundational: { bg: 'bg-app-positive/10', text: 'text-app-positive' },
  intermediate: { bg: 'bg-app-focus/10', text: 'text-app-focus' },
  advanced: { bg: 'bg-app-warning/10', text: 'text-app-warning' },
};

export default function CommonMistakesTab({ data }: Props) {
  if (!data?.summary && !data?.mistakes?.length) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-app-muted">No data available yet.</p>
        <p className="text-xs text-app-muted mt-1">Generate this module to populate content.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {data.summary && (
        <div className="bg-app-surface rounded-lg border border-app-line p-6">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-app-warning" />
            <h4 className="text-sm font-semibold text-app-ink">Overview</h4>
          </div>
          <FormattedText text={data.summary} className="text-sm text-app-ink" />
        </div>
      )}

      {data.mistakes?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-app-ink mb-4">Common Mistakes</h4>
          <div className="space-y-3">
            {data.mistakes.map((m, i) => {
              const sev = SEVERITY_STYLES[m.severity] || SEVERITY_STYLES.moderate;
              const lvl = LEVEL_STYLES[m.level_most_affected];
              return (
                <div key={i} className={`rounded-lg border p-4 ${
                  m.severity === 'critical' ? 'border-app-critical bg-app-surface shadow-none' : 'border-app-line bg-app-surface'
                }`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-start gap-2 flex-1">
                      <XCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${sev.text}`} />
                      <h5 className="text-sm font-semibold text-app-ink">{m.mistake}</h5>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {lvl && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${lvl.bg} ${lvl.text}`}>
                          {m.level_most_affected}
                        </span>
                      )}
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${sev.bg} ${sev.text}`}>
                        {sev.label}
                      </span>
                    </div>
                  </div>

                  {m.why_it_happens && (
                    <div className="flex items-start gap-2 mb-2 ml-6">
                      <Info className="w-3 h-3 text-app-muted mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-app-muted"><span className="font-medium">Why it happens:</span> {m.why_it_happens}</p>
                    </div>
                  )}

                  {m.how_to_fix && (
                    <div className="ml-6 bg-app-positive/10 border border-app-positive rounded-lg px-3 py-2">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Lightbulb className="w-3 h-3 text-app-positive" />
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-app-positive">How to Fix</span>
                      </div>
                      <p className="text-xs text-app-positive leading-relaxed">{m.how_to_fix}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.misconceptions?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-app-ink mb-3">Common Misconceptions</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.misconceptions.map((m, i) => (
              <div key={i} className="flex items-start gap-2 bg-app-warning/10 border border-app-warning rounded-lg p-3">
                <XCircle className="w-3.5 h-3.5 text-app-warning mt-0.5 flex-shrink-0" />
                <p className="text-xs text-app-warning">{m}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
